import React, { useEffect, useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
    Box,
    Dialog,
    Fade,
    PaperProps,
    Slide,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";

import {
    Actions,
    ActionsGroup,
    CloseButton,
    Content,
    Footer,
    Header,
    HeaderCenter,
    HeaderTopRow,
    IconWrap,
    StyledBackdrop,
    StyledPaper,
    Subtitle,
    Title,
    TitleGroup,
    TitleRow,
    TitleText,
} from "./GenericModal.styled";

// Transition component for smooth modal entrance
const SlideTransition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface GenericModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    icon?: React.ReactNode;
    content: React.ReactNode;
    /** Fixed footer rendered below the scrollable content area */
    footer?: React.ReactNode;
    /** Actions rendered inline in the header, left of the close button */
    headerActions?: React.ReactNode;

    /** Row rendered above the title row, inside the fixed header */
    headerTop?: React.ReactNode;
    /** Content centered in the title row, between the title and the actions */
    headerCenter?: React.ReactNode;
    /** Content rendered below the title row, inside the fixed header */
    headerBottom?: React.ReactNode;
    /** Style overrides for the dialog paper */
    paperSx?: SxProps<Theme>;
    /** Style overrides for the fixed header */
    headerSx?: SxProps<Theme>;
    /** Style overrides for the scrollable content area */
    contentSx?: SxProps<Theme>;
    /** Style overrides for the fixed footer */
    footerSx?: SxProps<Theme>;
    maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
    fullWidth?: boolean;
    fullScreen?: boolean;
    disableBackdropClick?: boolean;
    disableEscapeKeyDown?: boolean;
    transition?: "slide" | "fade";
    showCloseButton?: boolean;
    headerColor?: string;
    /**
     * Dialog height. Defaults to a responsive fixed height (90vh desktop,
     * 94vh below `md`, clamped to the viewport minus a 24px margin; mobile
     * is full screen).
     * Pass `"auto"` to size the dialog to its content.
     */
    fixedHeight?: string | number;
    minHeight?: string | number;
}

const GenericModal: React.FC<GenericModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    icon,
    content,
    footer,
    headerActions,
    headerTop,
    headerCenter,
    headerBottom,
    paperSx,
    headerSx,
    contentSx,
    footerSx,
    maxWidth = "sm",
    fullWidth = true,
    fullScreen = false,
    disableEscapeKeyDown = false,
    transition = "slide",
    showCloseButton = true,
    headerColor,
    fixedHeight,
    minHeight,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [contentKey, setContentKey] = useState(0);

    useEffect(() => {
        if (!open) {
            setContentKey((key) => key + 1);
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape" && !disableEscapeKeyDown) {
            onClose();
        }
    };

    const TransitionComponent = transition === "fade" ? Fade : SlideTransition;
    const isFullScreen = fullScreen || isMobile;
    const hasHeader =
        title ||
        icon ||
        showCloseButton ||
        headerActions ||
        headerTop ||
        headerCenter ||
        headerBottom;

    const paperSlotProps = {
        sx: paperSx,
        $fixedHeight: fixedHeight,
        $minHeight: minHeight,
    } as PaperProps;

    return (
        <Dialog
            open={open}
            keepMounted={false}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={isFullScreen}
            slots={{
                paper: StyledPaper,
                backdrop: StyledBackdrop,
                transition: TransitionComponent,
            }}
            slotProps={{
                paper: paperSlotProps,
                backdrop: { timeout: 300 },
                transition: { timeout: 300 },
            }}
            onKeyDown={handleKeyDown}
        >
            {hasHeader && (
                <Header
                    component="div"
                    $backgroundColor={headerColor}
                    sx={headerSx}
                >
                    {headerTop && <HeaderTopRow>{headerTop}</HeaderTopRow>}

                    <TitleRow $withCenter={Boolean(headerCenter)}>
                        <TitleGroup>
                            {icon && <IconWrap>{icon}</IconWrap>}
                            <TitleText>
                                {title && (
                                    <Title variant="h6" component="h2" noWrap>
                                        {title}
                                    </Title>
                                )}
                                {subtitle && (
                                    <Subtitle variant="body2">
                                        {subtitle}
                                    </Subtitle>
                                )}
                            </TitleText>
                        </TitleGroup>

                        {headerCenter && (
                            <HeaderCenter>{headerCenter}</HeaderCenter>
                        )}

                        <Actions>
                            {headerActions && (
                                <ActionsGroup>{headerActions}</ActionsGroup>
                            )}
                            {showCloseButton && (
                                <CloseButton
                                    onClick={onClose}
                                    size="small"
                                    aria-label="Close"
                                >
                                    <CloseIcon fontSize="small" />
                                </CloseButton>
                            )}
                        </Actions>
                    </TitleRow>

                    {headerBottom && <Box>{headerBottom}</Box>}
                </Header>
            )}

            {open && (
                <Content key={contentKey} sx={contentSx}>
                    {content}
                </Content>
            )}

            {open && footer && <Footer sx={footerSx}>{footer}</Footer>}
        </Dialog>
    );
};

export default GenericModal;
